'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import Link from 'next/link';
import { ArrowRight, X } from 'lucide-react';
import { useSession } from '@/lib/auth-client';
import { cn } from '@/lib/utils';
import { useDismissible } from '@/lib/hooks/useDismissible';
import { DirectoryListingDialog } from './DirectoryListingDialog';

/** Default LocalSettings key remembering that the callout was dismissed. */
const DEFAULT_DISMISS_KEY = 'directoryListingCallout.dismissed';

/**
 * Size/density variants for the callout:
 * - `default`: full card with generous padding, for section footers.
 * - `compact`: slimmer single-row banner, for top-of-page placement.
 * - `minimal`: very slim inline strip (no card chrome, description hidden),
 *   for tight spaces like toolbars or list headers.
 */
export type DirectoryListingCalloutVariant = 'default' | 'compact' | 'minimal';
interface DirectoryListingCalloutProps {
  /** Heading shown in the callout. */
  title?: string;
  /** Supporting line shown under the heading. Hidden in the `minimal` variant. */
  description?: string;
  /** Label for the action when the user is signed in. */
  loggedInLabel?: string;
  /** Label for the action when the user is signed out. */
  loggedOutLabel?: string;
  /**
   * Where signed-out users are sent to sign up. Defaults to the sign-in flow
   * with a redirect to the get-listed form page, so the form is shown once
   * they have an account.
   */
  signInHref?: string;
  /** Size/density variant. Defaults to `default`. */
  variant?: DirectoryListingCalloutVariant;
  /**
   * When true, shows a close button and hides the callout permanently once
   * dismissed. The dismissed state is persisted in LocalSettings.
   */
  dismissible?: boolean;
  /**
   * LocalSettings key used to remember dismissal. Override to scope dismissal
   * per placement. Defaults to {@link DEFAULT_DISMISS_KEY}.
   */
  dismissKey?: string;
  className?: string;
}

/**
 * Reusable "get listed on the directory" callout.
 *
 * When the user is signed in, the action opens {@link DirectoryListingDialog}
 * so they can submit their business details (emailed to the directory inbox).
 * When signed out, it links them to sign up. Used on the home page
 * (ExpertPartnersSection) and the /directory page.
 */
export function DirectoryListingCallout(t0) {
  const $ = _c(37);
  const {
    title: t1,
    description: t2,
    loggedInLabel: t3,
    loggedOutLabel: t4,
    signInHref: t5,
    variant: t6,
    dismissible: t7,
    dismissKey: t8,
    className
  } = t0;
  const title = t1 === undefined ? "Want to be on the directory?" : t1;
  const description = t2 === undefined ? "Get discovered and connect with opportunities" : t2;
  const loggedInLabel = t3 === undefined ? "Get listed" : t3;
  const loggedOutLabel = t4 === undefined ? "Sign up" : t4;
  const signInHref = t5 === undefined ? "/auth/signin?redirect=/directory/get-listed" : t5;
  const variant = t6 === undefined ? "default" : t6;
  const dismissible = t7 === undefined ? false : t7;
  const dismissKey = t8 === undefined ? DEFAULT_DISMISS_KEY : t8;
  const {
    data: session
  } = useSession();
  const isLoggedIn = !!session?.user;
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    isDismissed,
    hasLoaded,
    dismiss
  } = useDismissible(dismissKey);
  const isMinimal = variant === "minimal";
  const isCompact = variant === "compact";
  if (dismissible && (!hasLoaded || isDismissed)) {
    return null;
  }
  const t9 = isMinimal ? "px-4 py-2 text-sm" : isCompact ? "px-5 py-2.5 text-sm" : "px-7 py-3.5 text-base";
  let t10;
  if ($[0] !== t9) {
    t10 = cn("inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-full bg-gray-900 font-black text-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl", t9);
    $[0] = t9;
    $[1] = t10;
  } else {
    t10 = $[1];
  }
  const actionClass = t10;
  let t11;
  if ($[2] !== actionClass || $[3] !== isLoggedIn || $[4] !== loggedInLabel || $[5] !== loggedOutLabel || $[6] !== signInHref) {
    t11 = isLoggedIn ? <button type="button" onClick={() => setDialogOpen(true)} className={actionClass}>{loggedInLabel}<ArrowRight className="h-4 w-4" aria-hidden="true" /></button> : <Link href={signInHref} className={actionClass}>{loggedOutLabel}<ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>;
    $[2] = actionClass;
    $[3] = isLoggedIn;
    $[4] = loggedInLabel;
    $[5] = loggedOutLabel;
    $[6] = signInHref;
    $[7] = t11;
  } else {
    t11 = $[7];
  }
  const action = t11;
  const absoluteClose = dismissible && !isMinimal && !isCompact;
  const t12 = absoluteClose && "relative";
  let t13;
  if ($[8] !== absoluteClose || $[9] !== className || $[10] !== isCompact || $[11] !== isMinimal || $[12] !== t12) {
    t13 = cn("flex flex-col", t12, isMinimal ? "items-center gap-2 text-center sm:flex-row sm:justify-between sm:text-left" : cn("rounded-2xl border border-gray-200 bg-gray-50 text-center sm:flex-row sm:justify-between sm:text-left", isCompact ? "items-center gap-3 px-4 py-3 sm:px-6" : "items-center gap-4 px-6 py-8"), absoluteClose && "pr-12", className);
    $[8] = absoluteClose;
    $[9] = className;
    $[10] = isCompact;
    $[11] = isMinimal;
    $[12] = t12;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  const containerClass = t13;
  const t14 = isMinimal ? "text-sm sm:text-base" : isCompact ? "text-base sm:text-lg" : "text-xl sm:text-2xl";
  let t15;
  if ($[14] !== t14) {
    t15 = cn("font-extrabold tracking-tight text-gray-900", t14);
    $[14] = t14;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  const titleClass = t15;
  let t16;
  if ($[16] !== title || $[17] !== titleClass) {
    t16 = <h3 className={titleClass}>{title}</h3>;
    $[16] = title;
    $[17] = titleClass;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] !== description || $[20] !== isMinimal) {
    t17 = !isMinimal && <p className="mt-1 text-sm text-gray-500">{description}</p>;
    $[19] = description;
    $[20] = isMinimal;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== t16 || $[23] !== t17) {
    t18 = <div>{t16}{t17}</div>;
    $[22] = t16;
    $[23] = t17;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== absoluteClose || $[26] !== action || $[27] !== dismiss || $[28] !== dismissible) {
    t19 = dismissible ? <div className="flex items-center gap-2 sm:gap-3">{action}<button type="button" onClick={dismiss} aria-label="Dismiss" className={cn("cursor-pointer rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-200/70 hover:text-gray-600", absoluteClose && "absolute right-3 top-3")}><X className="h-4 w-4" aria-hidden="true" /></button></div> : action;
    $[25] = absoluteClose;
    $[26] = action;
    $[27] = dismiss;
    $[28] = dismissible;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== dialogOpen) {
    t20 = <DirectoryListingDialog open={dialogOpen} onOpenChange={setDialogOpen} />;
    $[30] = dialogOpen;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== containerClass || $[33] !== t18 || $[34] !== t19 || $[35] !== t20) {
    t21 = <div className={containerClass}>{t18}{t19}{t20}</div>;
    $[32] = containerClass;
    $[33] = t18;
    $[34] = t19;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  return t21;
}